import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { AlertTriangle, ChevronDown, ChevronRight, Flame, MousePointerClick, XCircle } from 'lucide-react';

import { loadStorefrontView } from './api';
import { Empty } from './parts';
import { useLiveResource } from './useLive';
import { CHART, ChartTip, KpiRow, NUMBER, Panel, Share, ago, pointLabel, tipPoint } from './MobileParts';
import {
  DeviceMark, PathText, ReadState, StoreTable, StorefrontGate, StorefrontHead, clickedName, codeText, count, duration, st, stCount,
  useStoreNames, useStorefrontSite,
} from './StorefrontParts';

// How fast the store is for the people using it, and where it breaks: the
// browser's own speed measures (web vitals) as rated when they were taken, page
// load times, page errors, and the clicks that show frustration.

const VITALS = ['LCP', 'INP', 'CLS', 'FCP', 'TTFB'];
const FAILED = CHART.failed;

// A measure by what it means to a visitor; its short name stays beside it, small.
const vitalName = (name) => st(`perf.name_${name.toLowerCase()}`);

// CLS has no unit; the others are milliseconds.
function vitalValue(name, value) {
  if (value === null || value === undefined) return st('none');
  return name === 'CLS' ? Number(value).toFixed(value < 0.1 ? 3 : 2) : duration(value);
}

// A measure is good when three in four of its samples were rated good, and poor
// when more than one in four were rated poor, as the vitals themselves are judged.
function vitalTone(vital) {
  if (vital.samples === 0) return 'flat';
  if (vital.good / vital.samples >= 0.75) return 'good';
  if (vital.poor / vital.samples > 0.25) return 'poor';
  return 'fair';
}

function VitalCards({ vitals }) {
  const byName = new Map(vitals.map((vital) => [vital.name, vital]));
  return (
    <div className="xo-sf-vitals">
      {VITALS.filter((name) => byName.has(name)).map((name) => {
        const vital = byName.get(name);
        const tone = vitalTone(vital);
        const total = Math.max(1, vital.samples);
        return (
          <section key={name} className={`xo-sf-vital is-${tone}`}>
            <header>
              <span className="xo-sf-vital-name">{vitalName(name)}<i>{name}</i></span>
              <span className="xo-sf-vital-tone">{st(`perf.tone_${tone}`)}</span>
            </header>
            <strong>{vitalValue(name, vital.p75)}</strong>
            <small>{st(`perf.vital_${name.toLowerCase()}`)}</small>
            <span className="xo-sf-vital-bar" aria-label={st('perf.vital_split', { good: NUMBER.format(vital.good), fair: NUMBER.format(vital.needsImprovement), poor: NUMBER.format(vital.poor) })}>
              <i className="is-good" style={{ flexGrow: vital.good / total }} />
              <i className="is-fair" style={{ flexGrow: vital.needsImprovement / total }} />
              <i className="is-poor" style={{ flexGrow: vital.poor / total }} />
            </span>
            <small className="xo-sf-vital-foot">{st('perf.vital_foot', { median: vitalValue(name, vital.median), samples: NUMBER.format(vital.samples) })}</small>
          </section>
        );
      })}
    </div>
  );
}

// Each page's 75th percentile for the three measures people feel most.
function VitalsByPage({ rows }) {
  const pages = useMemo(() => {
    const byPath = new Map();
    for (const row of rows) {
      if (!byPath.has(row.path)) byPath.set(row.path, { path: row.path, samples: 0 });
      const page = byPath.get(row.path);
      page[row.name] = row.p75;
      page.samples = Math.max(page.samples, row.samples);
    }
    return [...byPath.values()];
  }, [rows]);
  const columns = [
    { key: 'path', label: st('perf.col_page'), primary: true, sortValue: (row) => row.path, render: (row) => <PathText path={row.path} /> },
    ...['LCP', 'INP', 'CLS'].map((name) => ({ key: name, label: vitalName(name), align: 'end', sortValue: (row) => (row[name] === undefined ? null : row[name]), render: (row) => vitalValue(name, row[name]) })),
    { key: 'samples', label: st('perf.col_samples'), align: 'end', wide: true, sortValue: (row) => row.samples, render: (row) => count(row.samples) },
  ];
  return <StoreTable columns={columns} rows={pages} rowKey={(row) => row.path} limit={10} initialSort={{ key: 'LCP', direction: 'desc' }} empty={<Empty title={st('perf.no_vitals')} />} />;
}

function VitalsByDevice({ rows }) {
  const devices = useMemo(() => {
    const byDevice = new Map();
    for (const row of rows) {
      const key = row.deviceType || 'unknown';
      if (!byDevice.has(key)) byDevice.set(key, { deviceType: row.deviceType });
      byDevice.get(key)[row.name] = row.p75;
    }
    return [...byDevice.values()];
  }, [rows]);
  if (devices.length === 0) return <Empty title={st('perf.no_vitals')} />;
  return (
    <ol className="xo-sf-device-vitals">
      {devices.map((device) => (
        <li key={device.deviceType || 'unknown'}>
          <DeviceMark device={device.deviceType} withLabel />
          {['LCP', 'INP', 'CLS'].map((name) => <span key={name}><small>{vitalName(name)}</small><b>{vitalValue(name, device[name])}</b></span>)}
        </li>
      ))}
    </ol>
  );
}

function ErrorsChart({ series, step }) {
  if (series.every((point) => point.errors === 0)) return <Empty title={st('perf.no_errors')} />;
  return (
    <div className="xo-mob-chart is-short">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={series} margin={{ top: 8, right: 18, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={CHART.grid} />
          <XAxis dataKey="at" tickFormatter={pointLabel(step)} tickLine={false} axisLine={false} minTickGap={24} tick={CHART.tick} />
          <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={CHART.tick} width={28} />
          <Tooltip cursor={CHART.cursor} animationDuration={120} content={<ChartTip title={tipPoint(step)} rows={(point) => [{ name: st('perf.errors'), color: FAILED, mark: 'bar', text: NUMBER.format(point.errors) }]} />} />
          <Bar dataKey="errors" fill={FAILED} radius={[3, 3, 0, 0]} maxBarSize={18} name={st('perf.errors')} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function TopErrors({ errors }) {
  const names = useStoreNames();
  const [open, setOpen] = useState(null);
  if (errors.length === 0) return <Empty title={st('perf.no_errors')} />;
  const now = Date.now();
  return (
    <ol className="xo-sf-errors">
      {errors.map((error) => {
        const key = `${error.message}|${error.source}|${error.line}`;
        const expanded = open === key;
        return (
          <li key={key} className={expanded ? 'is-open' : ''}>
            <button type="button" onClick={() => setOpen(expanded ? null : key)} aria-expanded={expanded}>
              <span className="xo-mob-rank-icon xo-sf-bad-icon"><AlertTriangle aria-hidden="true" /></span>
              <span className="xo-sf-errors-main">
                <strong>{error.message}</strong>
                <small title={error.source ? `${error.source}${error.line ? `:${error.line}` : ''}` : undefined}>
                  {stCount('pages_count', error.paths.length)} · {st('perf.last_seen', { when: ago(error.lastAt, now) })}
                </small>
              </span>
              <span className="xo-sf-errors-count"><strong>{NUMBER.format(error.count)}</strong><small>{stCount('sessions_count', error.sessions)}</small></span>
              <ChevronDown aria-hidden="true" className="xo-sf-errors-chev" />
            </button>
            {expanded && (
              <div className="xo-sf-errors-body">
                <span>{st('perf.on_pages')}</span>
                <span className="xo-sf-utm-chips">{error.paths.map((path) => <em key={path} title={path}>{names.pageOrPath(path)}</em>)}</span>
                <Link className="xo-mob-link" to={`/xomics/store/sessions?errors=true${error.paths[0] ? `&path=${encodeURIComponent(error.paths[0])}` : ''}`}>
                  {st('perf.watch_sessions')}<ChevronRight aria-hidden="true" />
                </Link>
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function Frustration({ rows, icon, empty }) {
  const names = useStoreNames();
  if (rows.length === 0) return <Empty title={empty} />;
  const most = Math.max(1, ...rows.map((row) => row.count));
  return (
    <ol className="xo-mob-rank xo-sf-rank">
      {rows.slice(0, 8).map((row, index) => (
        <li key={`${row.path}|${row.track || row.textKey || row.selector}|${index}`}>
          <span className="xo-mob-rank-icon">{icon}</span>
          <span className="xo-mob-rank-main">
            <strong>{clickedName(row)}</strong>
            <small>
              <Link className="xo-sf-inline-link" to={`/xomics/store/pages?path=${encodeURIComponent(row.path)}`} title={row.path}>{names.pageOrPath(row.path)}</Link>
              {' · '}{stCount('sessions_count', row.sessions)}
            </small>
          </span>
          <Share value={row.count} total={most} />
          <em>{NUMBER.format(row.count)}</em>
        </li>
      ))}
    </ol>
  );
}

function PerformanceView({ site, store }) {
  const [days, setDays] = useState(7);
  const load = useCallback(() => loadStorefrontView('performance', { site: site.code, days }), [site.code, days]);
  const report = useLiveResource(['store', 'store_traffic'], load, { reconcileMs: 120000 });
  const head = <StorefrontHead title={st('perf.title')} subtitle={st('perf.subtitle')} days={days} onDays={setDays} store={store} />;
  if (report.data === null) return <div className="xo-platform-page xo-mobile-page xo-sf-page">{head}<ReadState resource={report} label={st('perf.loading')} /></div>;
  const data = report.data;
  return (
    <div className="xo-platform-page xo-mobile-page xo-sf-page">
      {head}
      {report.error && <p className="xo-alert t-warn" role="alert">{report.error}</p>}
      <VitalCards vitals={data.vitals} />
      <KpiRow items={[
        { label: st('perf.kpi_load'), value: duration(data.load.median), detail: st('perf.kpi_load_detail', { p75: duration(data.load.p75), p95: duration(data.load.p95) }) },
        { label: st('perf.kpi_errors'), value: count(data.errors.total), detail: stCount('perf.kpi_error_sessions', data.errors.sessions), trend: data.errors.series.map((point) => point.errors) },
        { label: st('perf.kpi_rage'), value: count(data.rageClicks.reduce((sum, row) => sum + row.count, 0)), detail: st('perf.kpi_rage_detail') },
        { label: st('perf.kpi_dead'), value: count(data.deadClicks.reduce((sum, row) => sum + row.count, 0)), detail: st('perf.kpi_dead_detail') },
      ]} />
      <Panel title={st('perf.devices_title')} detail={st('perf.devices_detail')}>
        <VitalsByDevice rows={data.vitalsByDevice} />
      </Panel>
      <Panel title={st('perf.pages_title')} detail={st('perf.pages_detail')}>
        <VitalsByPage rows={data.vitalsByPage} />
      </Panel>
      <Panel title={st('perf.errors_title')} detail={st('perf.errors_detail')}>
        <ErrorsChart series={data.errors.series} step={data.step} />
        <TopErrors errors={data.errors.top} />
      </Panel>
      <div className="xo-mob-row is-halves is-even">
        <Panel title={st('perf.rage_title')} detail={st('perf.rage_detail')}>
          <Frustration rows={data.rageClicks} icon={<Flame aria-hidden="true" />} empty={st('perf.no_rage')} />
        </Panel>
        <Panel title={st('perf.dead_title')} detail={st('perf.dead_detail')}>
          <Frustration rows={data.deadClicks} icon={<XCircle aria-hidden="true" />} empty={st('perf.no_dead')} />
        </Panel>
      </div>
      {data.checkoutErrors.length > 0 && (
        <Panel title={st('perf.checkout_title')} detail={st('perf.checkout_detail')}
          aside={<Link className="xo-mob-link" to="/xomics/store/checkout-funnel">{st('overview.checkout_link')}<ChevronRight aria-hidden="true" /></Link>}>
          <ol className="xo-mob-rank xo-sf-rank">
            {data.checkoutErrors.map((error) => (
              <li key={`${error.step}|${error.code}`}>
                <span className="xo-mob-rank-icon xo-sf-bad-icon"><MousePointerClick aria-hidden="true" /></span>
                <span className="xo-mob-rank-main"><strong title={error.code || undefined}>{error.code ? codeText('checkout_error', error.code) : st('checkout.no_code')}</strong><small>{codeText('checkout_step', error.step)}</small></span>
                <Share value={error.count} total={Math.max(1, ...data.checkoutErrors.map((entry) => entry.count))} />
                <em>{NUMBER.format(error.count)}</em>
              </li>
            ))}
          </ol>
        </Panel>
      )}
      <p className="xo-mob-footnote">{st('perf.footnote')}</p>
    </div>
  );
}

export default function StorefrontPerformance() {
  const store = useStorefrontSite();
  return <StorefrontGate store={store}>{(site) => <PerformanceView site={site} store={store} />}</StorefrontGate>;
}
